// colDef/internshipColDef.tsx
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  InternshipStatus,
  InternshipType,
} from "@/services/internship.service";
import { formatDate } from "@/utils/formatDate.util";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";

const columnHelper = createColumnHelper<InternshipType>();

export type InternshipActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

// ─── Status pill config ───────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  InternshipStatus,
  { label: string; className: string }
> = {
  active: {
    label: "Active",
    className: "bg-gray-900 text-white",
  },
  pending: {
    label: "Pending",
    className: "bg-gray-100 text-gray-600",
  },
  completed: {
    label: "Completed",
    className: "bg-gray-100 text-gray-500",
  },
};

export const createInternshipColumns = (
  onActionClick: (payload: InternshipActionType) => void
) => [
  // Position — plain bold
  columnHelper.accessor("position", {
    header: "Position",
    cell: (info) => (
      <span className="text-sm font-medium">{info.getValue()}</span>
    ),
  }),

  // Company — amber
  columnHelper.accessor("company", {
    header: "Company",
    cell: (info) => (
      <span className="text-sm font-medium">{info.getValue()}</span>
    ),
  }),

  // Students — count
  columnHelper.display({
    id: "students",
    header: "Students",
    cell: (info) => {
      const count = info.row.original.students?.length || 0;
      return (
        <span className="text-sm font-semibold">
          {count} Student{count !== 1 ? "s" : ""}
        </span>
      );
    },
  }),

  // Dept — dark pill
  // Dept — count
  columnHelper.display({
    id: "departments",
    header: "Departments",
    cell: (info) => {
      const count = info.row.original.departments?.length || 0;
      return (
        <span className="rounded-md bg-gray-900 px-2.5 py-1 text-xs font-bold text-white">
          {count} Dept{count !== 1 ? "s" : ""}
        </span>
      );
    },
  }),

  // Year/Sem — two lines
  // Mode — text
  columnHelper.accessor("mode", {
    header: "Mode",
    cell: (info) => (
      <span className="text-sm font-medium">{info.getValue()}</span>
    ),
  }),

  // Duration — plain
  columnHelper.accessor("duration", {
    header: "Duration",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Stipend — green $/mo
  columnHelper.accessor("stipend", {
    header: "Stipend",
    cell: (info) => (
      <span className="whitespace-nowrap font-semibold text-green-600">
        ${info.getValue().toLocaleString()}/mo
      </span>
    ),
  }),

  // Start Date — YYYY-MM-DD
  columnHelper.accessor("startDate", {
    header: "Start Date",
    cell: (info) => (
      <span className="whitespace-nowrap text-sm">
        {formatDate(info.getValue(), { locale: "en-CA", dateStyle: "short" })}
      </span>
    ),
  }),

  // Status pill
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const cfg = STATUS_CONFIG[info.getValue()];
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: (info) => {
      const data = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({
                id: data._id,
                name: data.position,
                action: "edit",
              })
            }
            aria-label="Edit internship"
          >
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({
                id: data._id,
                name: data.position,
                action: "delete",
              })
            }
            aria-label="Delete internship"
          >
            <Trash2 size={15} />
          </Button>
        </div>
      );
    },
  }),
];
